<template>
    <div class="relative">
        <label class="relative block">
            <input
                class="h-10 w-full flex items-center gap-2 text-left pl-3 text-slate-500 relative bg-white rounded border border-slate-200 py-1.5 placeholder:text-slate-500 placeholder:text-sm transition-colors text-sm focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                v-model="cardQuery"
                @focus="() => (showCardsList = true)"
                ref="creditCardUsedButton"
                v-click-outside="() => (showCardsList = false)"
            />
            <div
                class="w-full flex items-center gap-2 absolute pr-14 left-2 top-[50%] -translate-y-[calc(50%-0.125rem)] whitespace-nowrap truncate min-w-0 pointer-events-none text-slate-500 text-sm"
                v-if="
                    (!cardQuery && !showCardsList) ||
                    activeRedemption.connected_account_id
                "
            >
                <template v-if="activeRedemption.connected_account_id">
                    <ImageLoader>
                        <template #image>
                            <img
                                :src="`${cdnUrl}/${connectedAccount?.card?.card_image?.path}/_small/${connectedAccount?.card?.card_image?.file_name}`"
                                :alt="connectedAccount?.card?.title"
                                class="w-8 h-5 rounded-sm"
                            />
                        </template>
                        <template #error>
                            <Icon
                                icon="credit-card"
                                class="text-slate-600 w-8 h-5"
                            />
                        </template>
                    </ImageLoader>
                    <span class="truncate w-full overflow-hidden">
                        {{ connectedAccount?.card?.title }}
                        {{
                            connectedAccount?.last_four
                                ? `(${connectedAccount?.last_four})`
                                : ''
                        }}
                    </span>
                </template>
                <template v-else>Credit card used</template>
            </div>
            <button
                class="block leading-none text-red-600 w-5 h-5 absolute right-2 top-1/2 -translate-y-1/2 z-10"
                v-if="activeRedemption.connected_account_id"
                @click="clearCard()"
            >
                <Icon icon="close" />
            </button>
        </label>

        <Teleport to="body">
            <transition name="fade">
                <div
                    class="max-h-48 p-2 overflow-y-auto bg-white rounded-lg shadow-sm border border-slate-200 absolute z-[99999]"
                    v-show="showCardsList"
                    :style="`width: ${width}px; top: ${top}px; left: ${left}px`"
                >
                    <template
                        v-for="walletUser in filteredWalletUsers"
                        :key="walletUser.id"
                    >
                        <p
                            class="flex items-center gap-2 text-slate-500 text-sm font-medium mb-3"
                        >
                            <Icon icon="user" class="w-5 h-5 text-slate-500" />
                            {{ walletUser.name }}
                        </p>
                        <div
                            v-for="institution in walletUser.institutions"
                            :key="institution.id"
                        >
                            <p class="text-slate-500 text-sm mb-1 pt-1">
                                {{ institution.issuing_bank.name }}
                            </p>
                            <div class="mb-2 border-b border-slate-200 pb-2">
                                <label
                                    v-for="account in institution.accounts"
                                    :key="account.id"
                                    class="w-full flex items-center gap-2 px-3 py-2 text-slate-800 text-sm bg-white rounded hover:bg-slate-100 transition-colors mb-1 cursor-pointer"
                                    @click="
                                        showCardsList = false;
                                        cardQuery = null;
                                    "
                                >
                                    <input
                                        class="sr-only"
                                        type="radio"
                                        name="connected_account_id"
                                        :value="account.id"
                                        v-model="
                                            activeRedemption.connected_account_id
                                        "
                                    />
                                    <ImageLoader>
                                        <template #image>
                                            <img
                                                :src="`${cdnUrl}/${account?.card?.card_image?.path}/_small/${account?.card?.card_image?.file_name}`"
                                                class="w-8 h-5 rounded-sm"
                                                alt="Card image"
                                            />
                                        </template>
                                        <template #error>
                                            <Icon
                                                icon="credit-card"
                                                class="text-slate-600 w-8 h-5"
                                            />
                                        </template>
                                    </ImageLoader>
                                    {{ account.card?.title }}
                                    ({{ account.last_four }})
                                </label>
                            </div>
                        </div>
                    </template>
                </div>
            </transition>
        </Teleport>
    </div>
</template>
<script setup>
import { storeToRefs } from 'pinia';
import {
    onMounted,
    onBeforeUnmount,
    ref,
    useTemplateRef,
    watch,
    computed,
} from 'vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import { useTripsStore } from '@/store/trips';
import Icon from '@/components/ui/icon/icon.vue';

const tripsStore = useTripsStore();
const { activeRedemption, connectedAccount, wallet } = storeToRefs(tripsStore);

const cardQuery = ref(null);
const showCardsList = ref(false);
const creditCardUsedButton = useTemplateRef('creditCardUsedButton');
const top = ref(0);
const left = ref(0);
const width = ref(80);

// Debounce the search to prevent excessive re-renders
const debouncedCardQuery = computed(() => {
    return cardQuery.value;
});

// Use a more efficient filtering approach
const filteredWalletUsers = computed(() => {
    if (!debouncedCardQuery.value) return wallet.value.users;

    const query = debouncedCardQuery.value.toLowerCase();
    const results = [];

    for (const user of wallet.value.users) {
        const filteredInstitutions = [];

        for (const institution of user.institutions) {
            const filteredAccounts = institution.accounts.filter(
                (account) =>
                    account.card?.title?.toLowerCase().includes(query) ||
                    account.last_four?.includes(query) ||
                    institution.issuing_bank.name?.toLowerCase().includes(query)
            );

            if (filteredAccounts.length > 0) {
                filteredInstitutions.push({
                    ...institution,
                    accounts: filteredAccounts,
                });
            }
        }

        if (filteredInstitutions.length > 0) {
            results.push({
                ...user,
                institutions: filteredInstitutions,
            });
        }
    }

    return results;
});

const cdnUrl = computed(() => {
    return window.cdnUrl;
});

const setCardsDropdownPosition = () => {
    const position = creditCardUsedButton.value.getBoundingClientRect();
    top.value = position.top + 42;
    left.value = position.left;
    width.value = position.width;
};

watch(showCardsList, (val) => {
    if (val) {
        setCardsDropdownPosition();
    }
});

const clearCard = () => {
    activeRedemption.value.connected_account_id = null;
    cardQuery.value = '';
    showCardsList.value = false;
};

onMounted(() => {
    window.addEventListener('resize', setCardsDropdownPosition);
    document
        .querySelector('.ui-modal-content')
        ?.addEventListener('scroll', setCardsDropdownPosition);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', setCardsDropdownPosition);
    document
        .querySelector('.ui-modal-content')
        ?.removeEventListener('scroll', setCardsDropdownPosition);
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
